import React, { useState, useEffect } from 'react';
import { useToast } from '../hooks/use-toast';
import { jobsAPI, globalPromptsAPI, organizationsAPI } from '../services/api';
import type { Organization } from '../types';
import { theme } from '../styles/theme';
import { useChartColors, statusColor } from '../styles/chartColors';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface AnalyticsData {
  overall: {
    total_jobs: number;
    completed_jobs: number;
    failed_jobs: number;
    cancelled_jobs: number;
    active_jobs: number;
    success_rate: number;
  };
  costs: {
    total_usd: number;
    total_sgd: number;
    average_usd: number;
    average_sgd: number;
  };
  tokens: {
    total_tokens: number;
    average_tokens: number;
  };
  performance: {
    avg_processing_time_seconds: number;
    avg_processing_time_minutes: number;
  };
  rows: {
    total_processed: number;
    average_per_job: number;
  };
  charts: {
    // `color` is still sent by the endpoint but deliberately ignored: it put
    // the chart palette in the backend, where no token or contrast check
    // could reach it. The slice colour comes from `status` now (ADR-057).
    status_distribution: Array<{ status: string; count: number; color?: string }>;
    cost_timeline: Array<{ date: string; daily_cost_usd: number; daily_cost_sgd: number; job_count: number }>;
    token_timeline: Array<{ date: string; daily_tokens: number; job_count: number }>;
    model_distribution: Array<{ model: string; count: number }>;
    success_rate_timeline: Array<{ week: string; success_rate: number; total_jobs: number; completed_jobs: number }>;
  };
  classification: {
    'Part Category 1': Array<{ category: string; count: number }>;
    'Part Category 2': Array<{ category: string; count: number }>;
    'Part Category 3': Array<{ category: string; count: number }>;
  };
  date_range: {
    days: number;
    start_date: string;
    end_date: string;
  };
}

export default function Analytics() {
  const { toast } = useToast();
  // Categorical slots and the reserved status colours, resolved from the
  // tokens so they follow the theme (ADR-057).
  const chart = useChartColors();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [templates, setTemplates] = useState<Array<{ id: string; name: string; category: string }>>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [jobNameSearch, setJobNameSearch] = useState<string>('');
  const [jobNames, setJobNames] = useState<Array<{ id: string; name: string; created_at: string }>>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');

  useEffect(() => {
    loadTemplates();
    loadOrganizations();
  }, []);

  useEffect(() => {
    loadJobNames();
  }, [selectedCategory, selectedTemplate]);

  useEffect(() => {
    loadAnalytics();
  }, [days, selectedTemplate, selectedCategory, jobNameSearch, selectedOrganization]);

  const loadTemplates = async () => {
    try {
      const response = await globalPromptsAPI.list();
      setTemplates(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await organizationsAPI.list();
      // API returns array directly or in .data
      const orgData = Array.isArray(response.data) 
        ? response.data 
        : ((response.data as any)?.results || []);
      setOrganizations(orgData.filter((org: Organization) => org.is_active));
    } catch (error) {
      console.error('Failed to load organizations:', error);
    }
  };

  const loadJobNames = async () => {
    try {
      const response = await jobsAPI.jobNames(
        selectedCategory || undefined,
        selectedTemplate || undefined
      );
      setJobNames(response.data || []);
      // Reset job name selection when filters change
      setJobNameSearch('');
    } catch (error) {
      console.error('Failed to load job names:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await jobsAPI.analytics(
        days, 
        selectedTemplate || undefined,
        selectedCategory || undefined,
        jobNameSearch || undefined,
        selectedOrganization || undefined
      );
      setAnalytics(response.data as AnalyticsData);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast({ title: "Couldn't load analytics", variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-interactive-primary"></div>
          <p className="mt-4 text-text-secondary">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-text-secondary">
          <p>No analytics data available</p>
          <button
            onClick={loadAnalytics}
            className="mt-4 px-4 py-2 bg-interactive-primary text-text-inverse rounded-lg hover:bg-interactive-primary-hover"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h2 className={`text-3xl font-bold ${theme.text.primary}`}>Analytics Dashboard</h2>
        
        {/* Filters */}
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <label className={`text-sm font-medium ${theme.text.secondary}`}>Time Period:</label>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className={`px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-interactive-primary ${theme.text.primary} font-medium bg-bg-surface`}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <label className={`text-sm font-medium ${theme.text.secondary}`}>Organization:</label>
            <select
              value={selectedOrganization}
              onChange={(e) => setSelectedOrganization(e.target.value)}
              className={`px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-interactive-primary ${theme.text.primary} font-medium bg-bg-surface`}
            >
              <option value="">All Organizations</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <label className={`text-sm font-medium ${theme.text.secondary}`}>Category:</label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSelectedTemplate(''); // Reset template when category changes
              }}
              className={`px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-interactive-primary ${theme.text.primary} font-medium bg-bg-surface`}
            >
              <option value="">All Categories</option>
              {Array.from(new Set(templates.map(t => t.category))).sort().map((category) => (
                <option key={category} value={category}>
                  {category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <label className={`text-sm font-medium ${theme.text.secondary}`}>Global Template:</label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className={`px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-interactive-primary ${theme.text.primary} font-medium bg-bg-surface`}
            >
              <option value="">All Global Templates</option>
              {templates
                .filter(template => !selectedCategory || template.category === selectedCategory)
                .map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <label className={`text-sm font-medium ${theme.text.secondary}`}>Job/File Name:</label>
            <select
              value={jobNameSearch}
              onChange={(e) => setJobNameSearch(e.target.value)}
              className={`px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-interactive-primary ${theme.text.primary} font-medium bg-bg-surface`}
            >
              <option value="">All Jobs</option>
              {jobNames.map((job) => (
                <option key={job.id} value={job.name}>
                  {job.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Part Classification Analytics - SHOW FOR CLASSIFICATION CATEGORY OR CLASSIFICATION TEMPLATE */}
      {(() => {
        // Show classification charts if:
        // 1. Category filter is set to "classification" OR
        // 2. Selected template belongs to classification category
        const selectedTemplateData = templates.find(t => t.id === selectedTemplate);
        const showClassification = 
          selectedCategory === 'classification' || 
          (selectedTemplateData && selectedTemplateData.category === 'classification');
        
        return showClassification;
      })() && (
        <div className="mb-8">
          <h2 className={`text-2xl font-bold ${theme.text.primary} mb-4`}>AI Classification Results</h2>
          <p className={`${theme.text.secondary} mb-6`}>
            Distribution of parts by AI-classified categories for classification jobs
          </p>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Part Category 1 */}
            {analytics.classification['Part Category 1'] && analytics.classification['Part Category 1'].length > 0 && (
              <div className={`${theme.card.background} rounded-lg shadow p-6`}>
                <h3 className={`text-lg font-semibold ${theme.text.primary} mb-4`}>Part Category 1</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.classification['Part Category 1']}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill={chart.series[0]} name="Parts Count" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4">
                  <p className={`text-sm font-medium ${theme.text.secondary}`}>Top Categories:</p>
                  <ul className="mt-2 space-y-1">
                    {analytics.classification['Part Category 1'].slice(0, 3).map((item, idx) => (
                      <li key={idx} className={`text-sm ${theme.text.secondary}`}>
                        {idx + 1}. {item.category}: <span className="font-semibold">{item.count}</span> parts
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Part Category 2 */}
            {analytics.classification['Part Category 2'] && analytics.classification['Part Category 2'].length > 0 && (
              <div className={`${theme.card.background} rounded-lg shadow p-6`}>
                <h3 className={`text-lg font-semibold ${theme.text.primary} mb-4`}>Part Category 2</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.classification['Part Category 2']}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill={chart.series[0]} name="Parts Count" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4">
                  <p className={`text-sm font-medium ${theme.text.secondary}`}>Top Categories:</p>
                  <ul className="mt-2 space-y-1">
                    {analytics.classification['Part Category 2'].slice(0, 3).map((item, idx) => (
                      <li key={idx} className={`text-sm ${theme.text.secondary}`}>
                        {idx + 1}. {item.category}: <span className="font-semibold">{item.count}</span> parts
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Overview Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Jobs */}
        <div className={`${theme.card.background} rounded-lg shadow p-6`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${theme.text.secondary}`}>Total Jobs</p>
              <p className={`text-3xl font-bold ${theme.text.primary} mt-2`}>
                {analytics.overall.total_jobs}
              </p>
            </div>
            <div className="bg-interactive-primary/10 p-3 rounded-full">
              <svg className="w-6 h-6 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
          <p className="text-sm text-text-tertiary mt-2">
            {analytics.overall.active_jobs} active
          </p>
        </div>

        {/* Success Rate */}
        <div className={`${theme.card.background} rounded-lg shadow p-6`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${theme.text.secondary}`}>Success Rate</p>
              {/* Not the success tone: the colour was fixed while the number
                  is not, so a 37% success rate rendered green and read as
                  "good". A rate is a reading, and the reading is the number
                  (ADR-057). "Failed Jobs" below keeps the error tone, because
                  that one genuinely counts failures. */}
              <p className="text-3xl font-bold text-text-primary mt-2">
                {analytics.overall.success_rate}%
              </p>
            </div>
            <div className="bg-qc-success/10 p-3 rounded-full">
              <svg className="w-6 h-6 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="text-sm text-text-tertiary mt-2">
            {analytics.overall.completed_jobs} completed
          </p>
        </div>

        {/* Total Cost */}
        <div className={`${theme.card.background} rounded-lg shadow p-6`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${theme.text.secondary}`}>Total Cost</p>
              <p className={`text-3xl font-bold ${theme.text.primary} mt-2`}>
                ${analytics.costs.total_usd.toFixed(2)}
              </p>
            </div>
            <div className="bg-qc-warning/10 p-3 rounded-full">
              <svg className="w-6 h-6 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="text-sm text-text-tertiary mt-2">
            ${analytics.costs.average_usd.toFixed(4)} avg/job
          </p>
        </div>

        {/* Total Tokens */}
        <div className={`${theme.card.background} rounded-lg shadow p-6`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${theme.text.secondary}`}>Total Tokens</p>
              <p className={`text-3xl font-bold ${theme.text.primary} mt-2`}>
                {(analytics.tokens.total_tokens / 1000000).toFixed(2)}M
              </p>
            </div>
            <div className="bg-bg-subtle p-3 rounded-full">
              <svg className="w-6 h-6 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
          <p className="text-sm text-text-tertiary mt-2">
            {(analytics.tokens.average_tokens / 1000).toFixed(1)}k avg/job
          </p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Job Status Distribution (Pie Chart) */}
        <div className={`${theme.card.background} rounded-lg shadow p-6`}>
          <h2 className={`text-lg font-semibold ${theme.text.primary} mb-4`}>Job Status Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analytics.charts.status_distribution}
                dataKey="count"
                nameKey="status"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={(entry: any) => `${entry.status}: ${entry.count}`}
              >
                {analytics.charts.status_distribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={statusColor(entry.status, chart)} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Success Rate Trend */}
        <div className={`${theme.card.background} rounded-lg shadow p-6`}>
          <h2 className={`text-lg font-semibold ${theme.text.primary} mb-4`}>Success Rate Trend</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics.charts.success_rate_timeline}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="success_rate" fill={chart.series[0]} name="Success Rate (%)" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cost Over Time */}
        <div className={`${theme.card.background} rounded-lg shadow p-6`}>
          <h2 className={`text-lg font-semibold ${theme.text.primary} mb-4`}>Cost Trends (USD)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.charts.cost_timeline}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="daily_cost_usd" stroke={chart.series[0]} name="Daily Cost (USD)" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Token Usage Over Time */}
        <div className={`${theme.card.background} rounded-lg shadow p-6`}>
          <h2 className={`text-lg font-semibold ${theme.text.primary} mb-4`}>Token Usage Trends</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.charts.token_timeline}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="daily_tokens" stroke={chart.series[0]} name="Daily Tokens" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Model Distribution */}
      {analytics.charts.model_distribution.length > 0 && (
        <div className={`${theme.card.background} rounded-lg shadow p-6 mb-8`}>
          <h2 className={`text-lg font-semibold ${theme.text.primary} mb-4`}>Model Usage Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics.charts.model_distribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="model" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill={chart.series[0]} name="Jobs Count" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Part Classification Analytics */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-text-primary mb-4">AI Classification Results</h2>
        <p className="text-text-secondary mb-6">
          Distribution of parts by AI-classified categories across all completed jobs
        </p>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Part Category 1 */}
          {analytics.classification['Part Category 1'] && analytics.classification['Part Category 1'].length > 0 && (
            <div className={`${theme.card.background} rounded-lg shadow p-6`}>
              <h3 className={`text-lg font-semibold ${theme.text.primary} mb-4`}>Part Category 1</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.classification['Part Category 1']}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill={chart.series[0]} name="Parts Count" />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4">
                <p className="text-sm font-medium text-text-secondary">Top Categories:</p>
                <ul className="mt-2 space-y-1">
                  {analytics.classification['Part Category 1'].slice(0, 3).map((item, idx) => (
                    <li key={idx} className="text-sm text-text-secondary">
                      {idx + 1}. {item.category}: <span className="font-semibold">{item.count}</span> parts
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Part Category 2 */}
          {analytics.classification['Part Category 2'] && analytics.classification['Part Category 2'].length > 0 && (
            <div className={`${theme.card.background} rounded-lg shadow p-6`}>
              <h3 className={`text-lg font-semibold ${theme.text.primary} mb-4`}>Part Category 2</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.classification['Part Category 2']}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill={chart.series[0]} name="Parts Count" />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4">
                <p className="text-sm font-medium text-text-secondary">Top Categories:</p>
                <ul className="mt-2 space-y-1">
                  {analytics.classification['Part Category 2'].slice(0, 3).map((item, idx) => (
                    <li key={idx} className="text-sm text-text-secondary">
                      {idx + 1}. {item.category}: <span className="font-semibold">{item.count}</span> parts
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Performance Metrics */}
      <div className={`${theme.card.background} rounded-lg shadow p-6`}>
        <h2 className={`text-lg font-semibold ${theme.text.primary} mb-4`}>Performance Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className={`text-sm font-medium ${theme.text.secondary}`}>Avg Processing Time</p>
            <p className={`text-2xl font-bold ${theme.text.primary} mt-2`}>
              {analytics.performance.avg_processing_time_minutes.toFixed(1)} min
            </p>
            <p className="text-sm text-text-tertiary">
              ({analytics.performance.avg_processing_time_seconds.toFixed(0)} seconds)
            </p>
          </div>
          <div>
            <p className={`text-sm font-medium ${theme.text.secondary}`}>Total Rows Processed</p>
            <p className={`text-2xl font-bold ${theme.text.primary} mt-2`}>
              {analytics.rows.total_processed.toLocaleString()}
            </p>
            <p className="text-sm text-text-tertiary">
              {analytics.rows.average_per_job.toLocaleString()} avg/job
            </p>
          </div>
          <div>
            <p className={`text-sm font-medium ${theme.text.secondary}`}>Failed Jobs</p>
            <p className="text-2xl font-bold text-qc-error mt-2">
              {analytics.overall.failed_jobs}
            </p>
            <p className="text-sm text-text-tertiary">
              {analytics.overall.cancelled_jobs} cancelled
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
